import React, { useEffect, useState } from "react";
import AuthenticatedImage from "../../../components/AuthenticatedImage";
import {
  Table,
  Button,
  Form,
  Spinner,
  Badge,
  Row,
  Col,
  Modal,
  ListGroup,
  Alert,
  Pagination,
} from "react-bootstrap";
import {
  PencilSquare,
  PlusCircle,
  XCircleFill,
  CheckCircleFill,
  InfoCircle,
  FileEarmarkText,
  Trash,
  QuestionCircle,
  Search,
  JournalBookmarkFill,
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
  Paper,
  Chip,
} from "@mui/material";
import axios from "axios";
import AddClassOffcanvas from "./AddClassOffcanvas";
import AddMediaOffcanvas from "./AddMediaOffcanvas";
import AddNotesOffcanvas from "./AddNotesOffcanvas";
import { toast, ToastContainer } from "react-toastify";
import { useForm } from "react-hook-form";
import API_BASE_URL from "../../../config/api";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const positiveWholeNumberMessage =
  "maxParticipants must be a positive whole number";

const getApiErrorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (typeof data?.message === "string") return data.message;
  if (Array.isArray(data?.errors)) {
    return data.errors
      .map((item) => item?.message || item)
      .filter(Boolean)
      .join("\n");
  }
  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().filter(Boolean).join("\n");
  }
  return fallback;
};

const getAvailabilityLabel = (cls) => {
  if (cls?.isFullyBooked) return "Fully booked";
  if (cls?.maxParticipants == null) return "Unlimited spots";
  return `${cls?.registrationCount || 0} / ${cls.maxParticipants} spots filled`;
};

const ClassesWorkshops = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [allLocations, setAllLocations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showViewQuestionsModal, setShowViewQuestionsModal] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [sessionPage, setSessionPage] = useState(1);
  const [sessionsPerPage] = useState(5);

  const [editModalInstructors, setEditModalInstructors] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [noInstructorsAvailable, setNoInstructorsAvailable] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm();

  const fetchInstructors = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${API_BASE_URL}/api/admin/getRegister/instructor`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setInstructors(res.data?.users || []);
    } catch (error) {
      console.error("Error fetching instructors:", error);
      setInstructors([]);
    }
  };

  const convert24To12 = (time24) => {
    if (!time24) return { hour: "", minute: "", ampm: "" };

    let [hours, minutes] = time24.split(":");
    hours = parseInt(hours);

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours || 12;

    return {
      hour: hours.toString(),
      minute: minutes,
      ampm,
    };
  };

  useEffect(() => {
    const fetchInstructorsByLocation = async () => {
      if (!selectedClass?.location?._id) {
        setEditModalInstructors([]);
        setNoInstructorsAvailable(false);
        return;
      }

      setLoadingInstructors(true);
      setNoInstructorsAvailable(false);

      try {
        const token = localStorage.getItem("adminToken");

        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/instructor/getByLocation/${selectedClass.location._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (response.data?.data?.length > 0) {
            setEditModalInstructors(response.data.data);
            return;
          }
        } catch (apiError) {
          console.log("Location-specific API failed, trying fallback");
        }

        const allInstructorsRes = await axios.get(
          `${API_BASE_URL}/api/admin/getRegister/instructor`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const filtered =
          allInstructorsRes.data?.users?.filter(
            (inst) =>
              inst.location && inst.location._id === selectedClass.location._id,
          ) || [];

        if (filtered.length > 0) {
          setEditModalInstructors(filtered);
        } else {
          setNoInstructorsAvailable(true);
          setEditModalInstructors([]);
        }
      } catch (error) {
        console.error("Error fetching instructors:", error);
        toast.error("Failed to load instructors");
        setEditModalInstructors([]);
      } finally {
        setLoadingInstructors(false);
      }
    };

    if (selectedClass?.location?._id) {
      fetchInstructorsByLocation();
    }
  }, [selectedClass?.location?._id]);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filterLocation && { filterLocation: filterLocation }),
        ...(filterStatus && { status: filterStatus }),
        ...(search && { search }),
      };

      const res = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getAllClasses`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );

      setClasses(res.data?.data || []);
      setPagination((prev) => ({
        ...prev,
        total: res.data?.total || 0,
        page: res.data?.page || 1,
        limit: res.data?.limit || 10,
      }));
    } catch (err) {
      console.error("Error fetching classes:", err);
      setClasses([]);
      toast.error("Failed to fetch classes");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClass = async () => {
    const form = document.getElementById("editClassForm");
    if (!form) {
      toast.error("Form not found");
      return;
    }

    // Get form elements by name using form.elements
    const startHourField = form.elements.namedItem("startTimeHour");
    const startMinuteField = form.elements.namedItem("startTimeMinute");
    const endHourField = form.elements.namedItem("endTimeHour");
    const endMinuteField = form.elements.namedItem("endTimeMinute");

    const startHour = startHourField?.value?.trim() || "";
    const startMinute = startMinuteField?.value?.trim() || "";
    const endHour = endHourField?.value?.trim() || "";
    const endMinute = endMinuteField?.value?.trim() || "";

    if (!startHour || !startMinute) {
      toast.error("Please select start time (hour and minute)");
      return;
    }

    if (!endHour || !endMinute) {
      toast.error("Please select end time (hour and minute)");
      return;
    }

    const startTime = `${startHour}:${startMinute}`;
    const endTime = `${endHour}:${endMinute}`;

    const titleField = form.elements.namedItem("title");
    const themeField = form.elements.namedItem("theme");
    const sessionDateField = form.elements.namedItem("sessionDate");
    const sessionTypeField = form.elements.namedItem("sessionType");
    const locationField = form.elements.namedItem("location");
    const instructorField = form.elements.namedItem("instructor");
    const typeField = form.elements.namedItem("Type");
    const imageField = form.elements.namedItem("image");
    const maxParticipantsField = form.elements.namedItem("maxParticipants");
    const maxParticipants = maxParticipantsField?.value?.trim() || "";

    if (maxParticipants && !/^[1-9]\d*$/.test(maxParticipants)) {
      toast.error(positiveWholeNumberMessage);
      return;
    }

    const formData = new FormData();
    formData.append("title", titleField?.value || "");
    formData.append("theme", themeField?.value || "");
    formData.append("tags", JSON.stringify([]));

    formData.append(
      "startDate",
      sessionDateField?.value || new Date().toISOString().split("T")[0],
    );

    formData.append(
      "endDate",
      sessionDateField?.value || new Date().toISOString().split("T")[0],
    );

    formData.append("sessionType", sessionTypeField?.value || "weekly");
    formData.append("startTime", startTime);
    formData.append("endTime", endTime);
    formData.append("location", locationField?.value || "");
    formData.append("Instructor", instructorField?.value || "");
    formData.append("Type", typeField?.value || "");
    formData.append("maxParticipants", maxParticipants || "null");

    if (imageField?.files?.[0]) {
      formData.append("Image", imageField.files[0]);
    } else if (selectedClass?.Image) {
      formData.append("Image", selectedClass.Image);
    }

    try {
      const token = localStorage.getItem("adminToken");

      await axios.put(
        `${API_BASE_URL}/api/AdminClasses/updateClass/${selectedClass._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success("Class updated successfully!");
      setShowEditModal(false);
      fetchClasses();
    } catch (err) {
      console.error("Update error:", err);
      toast.error(getApiErrorMessage(err, "Failed to update class"));
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/location/admin/getAllLocations`,
      );

      const activeLocations = (response.data?.data || []).filter(
        (location) => location.status === "Active",
      );

      setAllLocations(activeLocations);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Failed to load locations");
    }
  };

  const fetchQuestions = async (classId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(
        `${API_BASE_URL}/api/questionaire/getQuestions/${classId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success && res.data.questions) {
        setQuestions(res.data.questions);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
      setQuestions([]);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchLocations();
    fetchInstructors();
  }, [pagination.page, pagination.limit, filterLocation, filterStatus, search]);

  const toggleStatus = async (id) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.patch(
        `${API_BASE_URL}/api/AdminClasses/blockClass/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setClasses((prev) =>
        prev.map((cls) =>
          cls._id === id
            ? {
                ...cls,
                status: response.data.data.status,
              }
            : cls,
        ),
      );

      toast.success(`Status changed to ${response.data.data.status}`);
    } catch (err) {
      console.error("Status toggle failed:", err);
      toast.error("Failed to update status");
    }
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const fetchInstructorsByLocation = async (locationId) => {
    if (!locationId) {
      setEditModalInstructors([]);
      setNoInstructorsAvailable(false);
      return;
    }

    setLoadingInstructors(true);
    setNoInstructorsAvailable(false);

    try {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/instructor/getByLocation/${locationId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (response.data?.data?.length > 0) {
          setEditModalInstructors(response.data.data);
          return;
        }
      } catch (apiError) {
        console.log("Location-specific API failed, trying fallback");
      }

      const allInstructorsRes = await axios.get(
        `${API_BASE_URL}/api/admin/getRegister/instructor`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const filtered =
        allInstructorsRes.data?.users?.filter(
          (inst) => inst.location && inst.location._id === locationId,
        ) || [];

      if (filtered.length > 0) {
        setEditModalInstructors(filtered);
      } else {
        setNoInstructorsAvailable(true);
        setEditModalInstructors([]);
      }
    } catch (error) {
      console.error("Error fetching instructors:", error);
      toast.error("Failed to load instructors");
      setEditModalInstructors([]);
    } finally {
      setLoadingInstructors(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(
        `${API_BASE_URL}/api/AdminClasses/deleteClass/${itemToDelete}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setClasses((prev) => prev.filter((cls) => cls._id !== itemToDelete));
      setShowDeleteModal(false);
      toast.success("Class deleted successfully!");
      fetchClasses();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete class");
    }
  };

  const handleAddQuestion = async (classId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API_BASE_URL}/api/questionaire/addQuestions/${classId}`,
        { questionText },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Question added successfully!");
      setQuestionText("");
      fetchQuestions(classId);
    } catch (err) {
      console.error("Error adding question:", err);
      toast.error("Failed to add question");
    }
  };

  const handleEditQuestion = async (classId, questionId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        `${API_BASE_URL}/api/questionaire/editQuestion/${classId}/${questionId}`,
        { questionText },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Question updated successfully!");
      setQuestionText("");
      setEditingQuestion(null);
      fetchQuestions(classId);
    } catch (err) {
      console.error("Error editing question:", err);
      toast.error("Failed to update question");
    }
  };

  const filtered = classes.filter((cls) => {
    const matchesTitle = cls.title
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesLocation = filterLocation
      ? cls.location?._id === filterLocation
      : true;
    const matchesStatus = filterStatus
      ? cls.status?.toLowerCase() === filterStatus.toLowerCase()
      : true;
    return matchesTitle && matchesLocation && matchesStatus;
  });

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Classes & Workshops
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <JournalBookmarkFill size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Classes & Workshops
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {pagination.total} total entries
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={() => setShowAddForm(true)}
          className="d-flex align-items-center gap-2 fw-bold"
          style={{
            background: primaryGradient,
            border: "none",
            borderRadius: "10px",
            padding: "10px 20px",
            boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
          }}
        >
          <PlusCircle size={16} /> Add New
        </Button>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          </Col>

          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All Locations</option>
              {allLocations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Blocked">Blocked</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Table */}
      <Paper
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading classes...
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              No classes or workshops found.
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {[
                  "#",
                  "Image",
                  "Title",
                  "Theme",
                  "Type",
                  "Location",
                  "Availability",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => (
                <tr
                  key={item._id}
                  style={{ borderBottom: "1px solid #f0f4ff" }}
                >
                  <td
                    style={{
                      color: "#aab4cc",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {(pagination.page - 1) * pagination.limit + idx + 1}
                  </td>
                  <td>
                    <AuthenticatedImage
                      src={item.Image || "https://via.placeholder.com/40"}
                      width="40"
                      height="40"
                      alt="img"
                      style={{
                        borderRadius: "8px",
                        objectFit: "cover",
                        border: "2px solid #e4eaf8",
                      }}
                    />
                  </td>
                  <td style={{ fontWeight: 600, color: "#1a237e" }}>
                    {item.title}
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    {item.theme}
                  </td>
                  <td>
                    <Chip
                      label={item.Type}
                      size="small"
                      sx={{
                        backgroundColor: "#e4eaf8",
                        color: "#1a237e",
                        fontSize: "0.7rem",
                        height: "24px",
                      }}
                    />
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    {item.location?.location || "N/A"}
                  </td>
                  <td>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          item.isFullyBooked ? "bg-danger" : ""
                        }`}
                        style={{
                          background: item.isFullyBooked ? undefined : "#e4eaf8",
                          color: item.isFullyBooked ? undefined : "#1a237e",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        {getAvailabilityLabel(item)}
                      </span>
                      {item.availableSpots != null && !item.isFullyBooked && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#7b8ab8", fontSize: "0.68rem" }}
                        >
                          {item.availableSpots} available
                        </Typography>
                      )}
                    </Box>
                  </td>
                  <td>
                    <span
                      className={`badge px-3 py-2 rounded-pill ${
                        item.status === "Active" ? "bg-success" : "bg-danger"
                      }`}
                      style={{ fontSize: "0.75rem" }}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ActionBtn
                        tooltip={
                          item.status === "Active" ? "Deactivate" : "Activate"
                        }
                        color={item.status === "Active" ? "#c62828" : "#2e7d32"}
                        onClick={() => toggleStatus(item._id)}
                      >
                        {item.status === "Active" ? (
                          <XCircleFill size={17} />
                        ) : (
                          <CheckCircleFill size={17} />
                        )}
                      </ActionBtn>

                      <ActionBtn
                        tooltip="Edit"
                        color="#f57f17"
                        onClick={() => {
                          const firstSession = item.sessions?.[0];
                          let startTimeParts = firstSession
                            ? convert24To12(firstSession.startTime)
                            : item.startTime
                              ? convert24To12(item.startTime)
                              : { hour: "09", minute: "00", ampm: "AM" };
                          let endTimeParts = firstSession
                            ? convert24To12(firstSession.endTime)
                            : item.endTime
                              ? convert24To12(item.endTime)
                              : { hour: "05", minute: "00", ampm: "PM" };

                          setSelectedClass({
                            ...item,
                            startTimeParts,
                            endTimeParts,
                          });

                          if (item.location?._id) {
                            fetchInstructorsByLocation(item.location._id);
                          }
                          setShowEditModal(true);
                        }}
                      >
                        <PencilSquare size={16} />
                      </ActionBtn>

                      <ActionBtn
                        tooltip="Delete"
                        color="#c62828"
                        onClick={() => handleDeleteClick(item._id)}
                      >
                        <Trash size={16} />
                      </ActionBtn>

                      <ActionBtn
                        tooltip="Add Notes"
                        color="#2e7d32"
                        onClick={() => {
                          setSelectedClass(item);
                          setShowNotesForm(true);
                        }}
                      >
                        <FileEarmarkText size={16} />
                      </ActionBtn>

                      <ActionBtn
                        tooltip="Add Questions"
                        color="#0d47a1"
                        onClick={() => {
                          setSelectedClass(item);
                          setShowAddQuestionModal(true);
                          setQuestionText("");
                          fetchQuestions(item._id);
                        }}
                      >
                        <QuestionCircle size={16} />
                      </ActionBtn>

                      <ActionBtn
                        tooltip="View Details"
                        color="#6a1b9a"
                        onClick={() => {
                          setSelectedClass(item);
                          setShowDetailsModal(true);
                        }}
                      >
                        <InfoCircle size={16} />
                      </ActionBtn>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(pagination.page - 1) * pagination.limit + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{pagination.total}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => setPagination((prev) => ({ ...prev, page: 1 }))}
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Prev
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 1),
                  }))
                }
                disabled={pagination.page === 1}
                style={{ border: "none", background: "transparent" }}
              />

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === pagination.page}
                    onClick={() =>
                      setPagination((prev) => ({ ...prev, page: pageNum }))
                    }
                    style={
                      pageNum === pagination.page
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: "none", background: "transparent" }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: Math.min(prev.page + 1, totalPages),
                  }))
                }
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
              <Pagination.Last
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: totalPages }))
                }
                disabled={pagination.page === totalPages}
                style={{ border: "none", background: "transparent" }}
              />
            </Pagination>
          </Box>
        )}
      </Paper>

      {/* Offcanvas Components */}
      <AddClassOffcanvas
        show={showAddForm}
        handleClose={() => {
          setShowAddForm(false);
          setSelectedClass(null);
        }}
        onSaved={fetchClasses}
        selected={selectedClass}
      />
      <AddMediaOffcanvas
        show={showMediaForm}
        handleClose={() => setShowMediaForm(false)}
        classId={selectedClass?._id}
      />
      <AddNotesOffcanvas
        show={showNotesForm}
        handleClose={() => setShowNotesForm(false)}
        classId={selectedClass?._id}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <Trash size={18} /> Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          <Box
            sx={{
              background: "#fff3e0",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              p: 2,
              fontSize: "0.9rem",
              color: "#e65100",
            }}
          >
            ⚠ Are you sure you want to delete this class? This action cannot be
            undone.
          </Box>
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            style={{ borderRadius: "8px" }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <InfoCircle size={18} /> Class Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedClass && (
            <Box>
              <Row>
                <Col md={4}>
                  {selectedClass.Image && (
                    <Box textAlign="center" mb={3}>
                      <AuthenticatedImage
                        src={selectedClass.Image}
                        alt="Class"
                        style={{
                          width: "100%",
                          maxHeight: "200px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          border: "2px solid #e4eaf8",
                        }}
                      />
                    </Box>
                  )}

                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: "#f8f9ff",
                      borderRadius: "8px",
                      border: "1px solid #e4eaf8",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#1a237e",
                        mb: 1,
                      }}
                    >
                      {selectedClass.title}
                    </Typography>

                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      <Chip
                        label={selectedClass.Type}
                        size="small"
                        sx={{
                          backgroundColor: "#0d47a1",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                      <Chip
                        label={selectedClass.sessionType}
                        size="small"
                        sx={{
                          backgroundColor: "#00acc1",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                      <Chip
                        label={selectedClass.status}
                        size="small"
                        sx={{
                          backgroundColor:
                            selectedClass.status === "Active"
                              ? "#2e7d32"
                              : "#c62828",
                          color: "#fff",
                          fontSize: "0.7rem",
                        }}
                      />
                      {selectedClass.isFullyBooked && (
                        <Chip
                          label="Fully booked"
                          size="small"
                          sx={{
                            backgroundColor: "#c62828",
                            color: "#fff",
                            fontSize: "0.7rem",
                          }}
                        />
                      )}
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7a99",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <i
                          className="bi bi-geo-alt-fill"
                          style={{ color: "#c62828" }}
                        ></i>
                        {selectedClass.location?.location || "N/A"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7a99",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <i
                          className="bi bi-person-fill"
                          style={{ color: "#0d47a1" }}
                        ></i>
                        {selectedClass.Instructor?.name || "N/A"}
                      </Typography>
                    </Box>

                    {selectedClass.tags?.length > 0 && (
                      <Box mt={2}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#7b8ab8", display: "block", mb: 1 }}
                        >
                          Tags:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {selectedClass.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{
                                backgroundColor: "#e4eaf8",
                                color: "#1a237e",
                                fontSize: "0.7rem",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Col>

                <Col md={8}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9ff",
                      borderRadius: "8px",
                      border: "1px solid #e4eaf8",
                    }}
                  >
                 

                    <Row className="mb-3 gy-3">
                         <Col md={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "#1a237e",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Theme
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                        {selectedClass.theme || "-"}
                      </Typography>
                    </Col>
                    {selectedClass?.sessionDate && <Col md={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: "#1a237e",
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Session Date
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                        {new Date(
                          selectedClass.sessionDate,
                        ).toLocaleDateString()}
                      </Typography>
                    </Col>}
                    
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Start Date
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                          {new Date(
                            selectedClass.startDate || selectedClass.Date,
                          ).toLocaleDateString()}
                        </Typography>
                      </Col>
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          End Date
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                          {new Date(
                            selectedClass.endDate || selectedClass.Date,
                          ).toLocaleDateString()}
                        </Typography>
                      </Col>
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Availability
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: selectedClass.isFullyBooked
                                ? "#c62828"
                                : "#6b7a99",
                              fontWeight: selectedClass.isFullyBooked ? 700 : 400,
                            }}
                          >
                            {getAvailabilityLabel(selectedClass)}
                          </Typography>
                          {selectedClass.isFullyBooked && (
                            <Badge bg="danger">Fully booked</Badge>
                          )}
                        </Box>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Start Time
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                          {selectedClass.startTime}
                        </Typography>
                      </Col>
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          End Time
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                          {selectedClass.endTime}
                        </Typography>
                      </Col>
                    </Row>

                    {selectedClass.sessions?.length > 0 && (
                      <Box mb={3}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 2,
                          }}
                        >
                          Sessions
                        </Typography>
                        <div className="table-responsive">
                          <Table hover size="sm" className="mb-0 align-middle">
                            <thead style={{ background: primaryGradient }}>
                              <tr>
                                <th
                                  style={{
                                    color: "#fff",
                                    fontSize: "0.7rem",
                                    padding: "8px",
                                  }}
                                >
                                  Date
                                </th>
                                <th
                                  style={{
                                    color: "#fff",
                                    fontSize: "0.7rem",
                                    padding: "8px",
                                  }}
                                >
                                  Start Time
                                </th>
                                <th
                                  style={{
                                    color: "#fff",
                                    fontSize: "0.7rem",
                                    padding: "8px",
                                  }}
                                >
                                  End Time
                                </th>
                                <th
                                  style={{
                                    color: "#fff",
                                    fontSize: "0.7rem",
                                    padding: "8px",
                                  }}
                                >
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedClass.sessions
                                .slice(
                                  (sessionPage - 1) * sessionsPerPage,
                                  sessionPage * sessionsPerPage,
                                )
                                .map((session, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: "1px solid #f0f4ff",
                                    }}
                                  >
                                    <td
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#6b7a99",
                                      }}
                                    >
                                      {new Date(
                                        session.date,
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </td>
                                    <td
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#6b7a99",
                                      }}
                                    >
                                      {session.startTime}
                                    </td>
                                    <td
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#6b7a99",
                                      }}
                                    >
                                      {session.endTime}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge ${
                                          session.sessionStatus === "live"
                                            ? "bg-success"
                                            : session.sessionStatus ===
                                                "completed"
                                              ? "bg-secondary"
                                              : "bg-warning text-dark"
                                        }`}
                                        style={{
                                          fontSize: "0.65rem",
                                          padding: "4px 8px",
                                        }}
                                      >
                                        {session.sessionStatus || "N/A"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </div>

                        {/* Session Pagination */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={2}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#7b8ab8" }}
                          >
                            Showing {(sessionPage - 1) * sessionsPerPage + 1} to{" "}
                            {Math.min(
                              sessionPage * sessionsPerPage,
                              selectedClass.sessions.length,
                            )}{" "}
                            of {selectedClass.sessions.length} sessions
                          </Typography>
                          <Box display="flex" gap={1}>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() =>
                                setSessionPage((prev) => Math.max(prev - 1, 1))
                              }
                              disabled={sessionPage === 1}
                              style={{ padding: "2px 8px", fontSize: "0.7rem" }}
                            >
                              Prev
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() =>
                                setSessionPage((prev) =>
                                  Math.min(
                                    prev + 1,
                                    Math.ceil(
                                      selectedClass.sessions.length /
                                        sessionsPerPage,
                                    ),
                                  ),
                                )
                              }
                              disabled={
                                sessionPage >=
                                Math.ceil(
                                  selectedClass.sessions.length /
                                    sessionsPerPage,
                                )
                              }
                              style={{ padding: "2px 8px", fontSize: "0.7rem" }}
                            >
                              Next
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    <Row>
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Created
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                          {new Date(selectedClass.createdAt).toLocaleString()}
                        </Typography>
                      </Col>
                      <Col md={6}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Last Updated
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                          {new Date(selectedClass.updatedAt).toLocaleString()}
                        </Typography>
                      </Col>
                    </Row>
                  </Paper>
                </Col>
              </Row>
            </Box>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowDetailsModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedClass(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PencilSquare size={18} /> Edit Class
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {selectedClass ? (
            <Form id="editClassForm">
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Title</Form.Label>
                <Form.Control
                  style={fieldStyle}
                  name="title"
                  defaultValue={selectedClass.title}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Theme</Form.Label>
                <Form.Control
                  style={fieldStyle}
                  as="textarea"
                  rows={3}
                  name="theme"
                  defaultValue={selectedClass.theme}
                  required
                />
              </Form.Group>

              {/* <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Start Date</Form.Label>
                    <Form.Control
                      style={fieldStyle}
                      type="date"
                      
                      name="startDate"
                      defaultValue={
                        selectedClass.startDate
                          ? selectedClass.startDate.split("T")[0]
                          : ""
                      }
                      required
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>End Date</Form.Label>
                    <Form.Control
                      style={fieldStyle}
                      type="date"
                      name="endDate"
                      defaultValue={
                        selectedClass.endDate
                          ? selectedClass.endDate.split("T")[0]
                          : ""
                      }
                      required
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row> */}

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Session Type</Form.Label>
                    <Form.Select
                      style={fieldStyle}
                      name="sessionType"
                      defaultValue={selectedClass.sessionType || "weekly"}
                      required
                    >
                      <option value="weekly">Weekly</option>
                      <option value="daily">Daily</option>
                      <option value="monthly">Monthly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Type</Form.Label>
                    <Form.Select
                      style={fieldStyle}
                      name="Type"
                      defaultValue={selectedClass.Type || "Workshop"}
                      required
                    >
                      <option value="Regular Class">Regular Class</option>
                      <option value="Workshop">Workshop</option>
                      <option value="Special Event">Special Event</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {selectedClass?.sessionDate &&  <Col md={4}>
                  <Form.Group>
                    <Form.Label style={labelStyle}>Session Date</Form.Label>
                      <Form.Control
                  style={fieldStyle}
                  type="date"
                  defaultValue={new Date(selectedClass?.sessionDate).toISOString().split("T")[0]}
                  required
                />
                
                  </Form.Group>
                 
                </Col>
                 }
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} md={6}>
                  <Form.Label style={labelStyle}>Start Time</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      style={fieldStyle}
                      name="startTimeHour"
                      defaultValue={selectedClass.startTimeParts?.hour || ""}
                      required
                    >
                      <option value="">Hour</option>
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={String(i).padStart(2, "0")}>
                          {String(i).padStart(2, "0")}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      style={fieldStyle}
                      name="startTimeMinute"
                      defaultValue={selectedClass.startTimeParts?.minute || ""}
                      required
                    >
                      <option value="">Min</option>
                      {[
                        "00",
                        "05",
                        "10",
                        "15",
                        "20",
                        "25",
                        "30",
                        "35",
                        "40",
                        "45",
                        "50",
                        "55",
                      ].map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Form.Group>

                <Form.Group as={Col} md={6}>
                  <Form.Label style={labelStyle}>End Time</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      style={fieldStyle}
                      name="endTimeHour"
                      defaultValue={selectedClass.endTimeParts?.hour || ""}
                      required
                    >
                      <option value="">Hour</option>
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={String(i).padStart(2, "0")}>
                          {String(i).padStart(2, "0")}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      style={fieldStyle}
                      name="endTimeMinute"
                      defaultValue={selectedClass.endTimeParts?.minute || ""}
                      required
                    >
                      <option value="">Min</option>
                      {[
                        "00",
                        "05",
                        "10",
                        "15",
                        "20",
                        "25",
                        "30",
                        "35",
                        "40",
                        "45",
                        "50",
                        "55",
                      ].map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Form.Group>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Location</Form.Label>
                <Form.Select
                  style={fieldStyle}
                  name="location"
                  defaultValue={selectedClass.location?._id}
                  required
                  onChange={async (e) => {
                    const locationId = e.target.value;
                    setValue("location", locationId);
                    await fetchInstructorsByLocation(locationId);
                  }}
                >
                  <option value="">Select a location</option>
                  {allLocations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.location}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>
                  Maximum Participants / Available Spots
                </Form.Label>
                <Form.Control
                  style={fieldStyle}
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  name="maxParticipants"
                  defaultValue={selectedClass.maxParticipants ?? ""}
                  placeholder="Leave blank for unlimited spots"
                />
                <Form.Text muted>Leave empty for unlimited spots.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Instructor</Form.Label>
                {loadingInstructors ? (
                  <div className="d-flex align-items-center">
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                      style={{ color: "#0d47a1" }}
                    />
                    <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                      Loading instructors...
                    </Typography>
                  </div>
                ) : noInstructorsAvailable ? (
                  <Alert
                    variant="warning"
                    className="py-2 mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    No instructors available for this location
                  </Alert>
                ) : (
                  <Form.Select
                    style={fieldStyle}
                    name="instructor"
                    defaultValue={selectedClass.Instructor?._id}
                    required
                  >
                    <option value="">Select an instructor</option>
                    {editModalInstructors.map((instructor) => (
                      <option key={instructor._id} value={instructor._id}>
                        {instructor.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Image</Form.Label>
                <Form.Control
                  style={fieldStyle}
                  type="file"
                  name="image"
                  accept="image/*"
                />
                {selectedClass.Image && (
                  <Box mt={2}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#7b8ab8", display: "block", mb: 1 }}
                    >
                      Current Image:
                    </Typography>
                    <AuthenticatedImage
                      src={selectedClass.Image}
                      alt="Current class"
                      style={{
                        maxWidth: "100px",
                        maxHeight: "100px",
                        borderRadius: "4px",
                        border: "1px solid #e4eaf8",
                      }}
                    />
                  </Box>
                )}
              </Form.Group>
            </Form>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                Loading...
              </Typography>
            </Box>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowEditModal(false);
              setSelectedClass(null);
            }}
            style={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleUpdateClass}
            style={{
              background: primaryGradient,
              border: "none",
              borderRadius: "8px",
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Question Modal */}
      <Modal
        show={showAddQuestionModal}
        onHide={() => {
          setShowAddQuestionModal(false);
          setQuestionText("");
          setEditingQuestion(null);
          setQuestions([]);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <QuestionCircle size={18} />
            {editingQuestion
              ? "Edit Question"
              : `Add Questions for ${selectedClass?.title}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (editingQuestion) {
                handleEditQuestion(selectedClass._id, editingQuestion._id);
              } else {
                handleAddQuestion(selectedClass._id);
              }
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Question Text</Form.Label>
              <Form.Control
                style={fieldStyle}
                as="textarea"
                rows={3}
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                required
              />
            </Form.Group>
            <Box display="flex" justifyContent="flex-end" mb={3}>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setQuestionText("");
                  setEditingQuestion(null);
                }}
                className="me-2"
                style={{ borderRadius: "8px" }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="primary"
                style={{
                  background: primaryGradient,
                  border: "none",
                  borderRadius: "8px",
                }}
              >
                {editingQuestion ? "Update Question" : "Add Question"}
              </Button>
            </Box>
          </Form>

          <hr style={{ borderColor: "#e4eaf8" }} />

          <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 2 }}>
            Existing Questions
          </Typography>
          {questions.length === 0 ? (
            <Typography
              variant="caption"
              sx={{
                color: "#7b8ab8",
                display: "block",
                textAlign: "center",
                py: 2,
              }}
            >
              No questions added yet.
            </Typography>
          ) : (
            <ListGroup variant="flush">
              {questions.map((q, index) => (
                <ListGroup.Item
                  key={q._id}
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: "#f8f9ff",
                    border: "1px solid #e4eaf8",
                    borderRadius: "8px",
                    marginBottom: "4px",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <span
                      style={{
                        color: "#1a237e",
                        fontWeight: 600,
                        marginRight: "8px",
                      }}
                    >
                      {index + 1}.
                    </span>
                    <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                      {q.questionText}
                    </Typography>
                  </Box>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => {
                      setEditingQuestion(q);
                      setQuestionText(q.questionText);
                    }}
                    style={{
                      borderRadius: "6px",
                      padding: "2px 8px",
                      fontSize: "0.7rem",
                    }}
                  >
                    Edit
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowAddQuestionModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Questions Modal */}
      <Modal
        show={showViewQuestionsModal}
        onHide={() => setShowViewQuestionsModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <QuestionCircle size={18} /> Questions for {selectedClass?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: "1.5rem" }}>
          {questions.length === 0 ? (
            <Typography
              variant="caption"
              sx={{
                color: "#7b8ab8",
                display: "block",
                textAlign: "center",
                py: 2,
              }}
            >
              No questions added yet.
            </Typography>
          ) : (
            <ListGroup variant="flush">
              {questions.map((q, index) => (
                <ListGroup.Item
                  key={q._id}
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: "#f8f9ff",
                    border: "1px solid #e4eaf8",
                    borderRadius: "8px",
                    marginBottom: "4px",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <span
                      style={{
                        color: "#1a237e",
                        fontWeight: 600,
                        marginRight: "8px",
                      }}
                    >
                      {index + 1}.
                    </span>
                    <Typography variant="caption" sx={{ color: "#6b7a99" }}>
                      {q.questionText}
                    </Typography>
                  </Box>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => {
                      setEditingQuestion(q);
                      setQuestionText(q.questionText);
                      setShowViewQuestionsModal(false);
                      setShowAddQuestionModal(true);
                    }}
                    style={{
                      borderRadius: "6px",
                      padding: "2px 8px",
                      fontSize: "0.7rem",
                    }}
                  >
                    Edit
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{ background: "#f7f9ff", borderTop: "1px solid #e4eaf8" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowViewQuestionsModal(false)}
            style={{ borderRadius: "8px" }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setQuestionText("");
              setEditingQuestion(null);
              setShowViewQuestionsModal(false);
              setShowAddQuestionModal(true);
            }}
            style={{
              background: primaryGradient,
              border: "none",
              borderRadius: "8px",
            }}
          >
            Add New Question
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default ClassesWorkshops;
